import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import api from '../api';

const initialSystem = {
  name: '',
  systemKey: '',
  productName: '',
  category: 'erp',
  environment: 'production',
  authMode: 'oauth2',
  status: 'pending',
  licenseStatus: 'unknown',
  organizationId: '',
  baseUrl: '',
  adminUrl: '',
  supportContactEmail: '',
  planName: '',
  currentVersion: '',
  renewalDate: '',
  healthMessage: '',
};

export default function SystemRegistry() {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState(initialSystem);

  useEffect(() => {
    async function loadOrganizations() {
      setLoading(true);
      setError('');
      try {
        const response = await api.get('/admin/platform/organizations');
        setOrganizations(response.data.organizations || []);
      } catch (err) {
        setError(err?.response?.data?.message || 'Could not load organizations');
      } finally {
        setLoading(false);
      }
    }

    loadOrganizations();
  }, []);

  async function handleSubmit(event) {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      await api.post('/admin/platform/systems', {
        ...form,
        organizationId: form.organizationId || undefined,
      });
      navigate('/systems');
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not create system');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Layout searchPlaceholder="Search system registry...">
      <div className="space-y-8">
        <section className="space-y-2">
          <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#f1c100]">
            Systems Registry
          </span>
          <h1 className="font-['Geist'] text-[clamp(2rem,5vw,3rem)] font-black tracking-[-0.03em] text-white">
            Register Managed System
          </h1>
          <p className="max-w-3xl text-[#d2c5ab]">
            This form is kept off the monitoring screen so the Systems dashboard stays focused on telemetry, renewals, and operational health.
          </p>
        </section>

        {error ? (
          <div className="rounded-lg border border-[#93000a] bg-[#2a1112] px-4 py-3 text-sm text-[#ffdad6]">
            {error}
          </div>
        ) : null}

        <form
          onSubmit={handleSubmit}
          className="max-w-4xl space-y-6 rounded-lg border border-[#4e4632] bg-[#1c1b1b] p-6"
        >
          {loading ? (
            <div className="text-sm text-[#d2c5ab]">Loading organizations...</div>
          ) : null}

          <div className="grid gap-4 md:grid-cols-2">
            <InputField label="System name" value={form.name} onChange={(value) => setForm((prev) => ({ ...prev, name: value }))} />
            <InputField label="System key" value={form.systemKey} onChange={(value) => setForm((prev) => ({ ...prev, systemKey: value }))} />
            <InputField label="Product name" value={form.productName} onChange={(value) => setForm((prev) => ({ ...prev, productName: value }))} />
            <InputField label="Support contact email" value={form.supportContactEmail} onChange={(value) => setForm((prev) => ({ ...prev, supportContactEmail: value }))} />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <SelectField label="Category" value={form.category} onChange={(value) => setForm((prev) => ({ ...prev, category: value }))}>
              <option value="erp">ERP</option>
              <option value="crm">CRM</option>
              <option value="hrm">HRM</option>
              <option value="pos">POS</option>
              <option value="auth">Auth</option>
              <option value="finance">Finance</option>
              <option value="website">Website</option>
              <option value="custom">Custom</option>
            </SelectField>
            <SelectField label="Environment" value={form.environment} onChange={(value) => setForm((prev) => ({ ...prev, environment: value }))}>
              <option value="production">Production</option>
              <option value="staging">Staging</option>
              <option value="development">Development</option>
            </SelectField>
            <SelectField label="Auth mode" value={form.authMode} onChange={(value) => setForm((prev) => ({ ...prev, authMode: value }))}>
              <option value="oauth2">OAuth2</option>
              <option value="oidc">OIDC</option>
              <option value="api_key">API Key</option>
              <option value="hybrid">Hybrid</option>
              <option value="session">Session</option>
            </SelectField>
            <SelectField label="Status" value={form.status} onChange={(value) => setForm((prev) => ({ ...prev, status: value }))}>
              <option value="pending">Pending</option>
              <option value="healthy">Healthy</option>
              <option value="warning">Warning</option>
              <option value="offline">Offline</option>
            </SelectField>
            <SelectField label="License status" value={form.licenseStatus} onChange={(value) => setForm((prev) => ({ ...prev, licenseStatus: value }))}>
              <option value="unknown">Unknown</option>
              <option value="valid">Valid</option>
              <option value="trial">Trial</option>
              <option value="expiring">Expiring</option>
              <option value="expired">Expired</option>
            </SelectField>
            <label className="block">
              <span className="mb-2 block text-xs font-bold uppercase tracking-[0.18em] text-[#d2c5ab]">Organization</span>
              <select
                value={form.organizationId}
                onChange={(event) => setForm((prev) => ({ ...prev, organizationId: event.target.value }))}
                className="w-full rounded-lg border border-[#4e4632] bg-[#201f1f] px-4 py-3 text-white outline-none"
              >
                <option value="">Unassigned organization</option>
                {organizations.map((organization) => (
                  <option key={organization._id} value={organization._id}>
                    {organization.name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <InputField label="Plan name" value={form.planName} onChange={(value) => setForm((prev) => ({ ...prev, planName: value }))} />
            <InputField label="Current version" value={form.currentVersion} onChange={(value) => setForm((prev) => ({ ...prev, currentVersion: value }))} />
            <InputField label="Base URL" value={form.baseUrl} onChange={(value) => setForm((prev) => ({ ...prev, baseUrl: value }))} />
            <InputField label="Admin URL" value={form.adminUrl} onChange={(value) => setForm((prev) => ({ ...prev, adminUrl: value }))} />
            <label className="block">
              <span className="mb-2 block text-xs font-bold uppercase tracking-[0.18em] text-[#d2c5ab]">Renewal date</span>
              <input
                type="date"
                value={form.renewalDate}
                onChange={(event) => setForm((prev) => ({ ...prev, renewalDate: event.target.value }))}
                className="w-full rounded-lg border border-[#4e4632] bg-[#201f1f] px-4 py-3 text-white outline-none"
              />
            </label>
          </div>

          <label className="block">
            <span className="mb-2 block text-xs font-bold uppercase tracking-[0.18em] text-[#d2c5ab]">Health note</span>
            <textarea
              rows="4"
              value={form.healthMessage}
              onChange={(event) => setForm((prev) => ({ ...prev, healthMessage: event.target.value }))}
              className="w-full rounded-lg border border-[#4e4632] bg-[#201f1f] px-4 py-3 text-white outline-none"
            />
          </label>

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-[#ffcc00] px-6 py-3 text-sm font-bold uppercase tracking-[0.12em] text-[#3d2f00] transition hover:brightness-110 disabled:opacity-60"
            >
              {saving ? 'Saving...' : 'Create system'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/systems')}
              className="rounded-lg border border-[#4e4632] px-6 py-3 text-sm font-bold uppercase tracking-[0.12em] text-[#e5e2e1] transition hover:bg-[#201f1f]"
            >
              Back to monitoring
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}

function InputField({ label, value, onChange }) {
  return (
    <label className="block">
      <span className="mb-2 block text-xs font-bold uppercase tracking-[0.18em] text-[#d2c5ab]">{label}</span>
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="w-full rounded-lg border border-[#4e4632] bg-[#201f1f] px-4 py-3 text-white outline-none"
      />
    </label>
  );
}

function SelectField({ label, value, onChange, children }) {
  return (
    <label className="block">
      <span className="mb-2 block text-xs font-bold uppercase tracking-[0.18em] text-[#d2c5ab]">{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="w-full rounded-lg border border-[#4e4632] bg-[#201f1f] px-4 py-3 text-white outline-none"
      >
        {children}
      </select>
    </label>
  );
}
